import { useEffect, useState, useCallback } from 'react';
import { Bell, Mail, MessageSquare, Smartphone, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, ChevronDown, ChevronUp, AtSign, Phone } from 'lucide-react';
import { useCompany }     from '@/context/CompanyContext';
import { ExtraContactsCard } from './ExtraContactsCard';
import {
  notificationPreferencesService,
  type NotificationEventPreference,
} from '@/lib/notificationPreferencesService';

/* ─────────────────────────────────────────────────────────────────────────────
   Module metadata — labels and icons for each module group
───────────────────────────────────────────────────────────────────────────── */

const MODULE_META: Record<string, { label: string; color: string }> = {
  inventory: { label: 'ניהול מלאי',         color: 'bg-blue-100 text-blue-700' },
  hr:        { label: 'ניהול עובדים',       color: 'bg-green-100 text-green-700' },
  cashflow:  { label: 'תזרים מזומנים',      color: 'bg-amber-100 text-amber-700' },
  b2b:       { label: 'B2B מסחר',           color: 'bg-teal-100 text-teal-700' },
  system:    { label: 'מערכת',              color: 'bg-gray-100 text-gray-700' },
};

const CHANNEL_META: Array<{
  key:   'in_app' | 'email' | 'sms' | 'whatsapp';
  label: string;
  icon:  React.ReactNode;
  color: string;
}> = [
  { key: 'in_app',    label: 'במערכת',  icon: <Bell        className="w-4 h-4" />, color: 'text-blue-600' },
  { key: 'email',     label: 'מייל',    icon: <Mail        className="w-4 h-4" />, color: 'text-gray-600' },
  { key: 'sms',       label: 'SMS',     icon: <Smartphone  className="w-4 h-4" />, color: 'text-green-600' },
  { key: 'whatsapp',  label: 'WhatsApp',icon: <MessageSquare className="w-4 h-4" />, color: 'text-emerald-600' },
];

const COMING_SOON_EVENTS = new Set([
  'inventory.low_stock',
  'inventory.overdue_debt',
  'inventory.order_shipped',
  'system.invitation_accepted',
  'cashflow.recurring_overdue',
  'cashflow.weekly_summary',
  'cashflow.monthly_summary',
]);

const isComingSoon = (eventKey: string) =>
  eventKey.startsWith('hr.') || COMING_SOON_EVENTS.has(eventKey);

const normalizeEmail = (raw: string) => {
  const v = raw.trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : null;
};

const normalizePhone = (raw: string) => {
  let digits = raw.replace(/[^\d]/g, '');
  if (raw.trim().startsWith('00')) digits = digits.replace(/^00/, '');
  if (digits.startsWith('9720')) digits = '972' + digits.slice(4);
  else if (digits.startsWith('0')) digits = '972' + digits.slice(1);
  if (digits.startsWith('972') && digits.length !== 12) return null;
  if (digits.length < 9 || digits.length > 15) return null;
  return `+${digits}`;
};

const displayPhone = (phone: string) => {
  const m = phone.match(/^\+972(\d{2})(\d{3})(\d{4})$/);
  return m ? `0${m[1]}-${m[2]}-${m[3]}` : phone;
};

/* ─────────────────────────────────────────────────────────────────────────────
   Toggle switch component
───────────────────────────────────────────────────────────────────────────── */

function Toggle({ checked, onChange, disabled }: {
  checked:  boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
        ${checked ? 'bg-blue-600' : 'bg-gray-200'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform
          transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Module section (collapsible)
───────────────────────────────────────────────────────────────────────────── */

function ModuleSection({
  module,
  events,
  onToggle,
  saving,
}: {
  module:   string;
  events:   NotificationEventPreference[];
  onToggle: (eventKey: string, channel: 'in_app' | 'email' | 'sms' | 'whatsapp', value: boolean) => void;
  saving:   Set<string>;
}) {
  const [open, setOpen] = useState(true);
  const meta = MODULE_META[module] ?? { label: module, color: 'bg-gray-100 text-gray-700' };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${meta.color}`}>
            {meta.label}
          </span>
          <span className="text-sm text-gray-500">{events.length} אירועים</span>
        </div>
        {open
          ? <ChevronUp   className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {/* Table */}
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-white">
                <th className="text-right px-5 py-3 font-semibold text-gray-600 w-1/2">
                  אירוע
                </th>
                {CHANNEL_META.map((ch) => (
                  <th key={ch.key} className="px-4 py-3 text-center font-semibold text-gray-600 min-w-[80px]">
                    <div className={`flex items-center justify-center gap-1.5 ${ch.color}`}>
                      {ch.icon}
                      <span className="text-xs">{ch.label}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((ev, idx) => {
                const isSaving = saving.has(ev.event_key);
                const soon = isComingSoon(ev.event_key);
                return (
                  <tr
                    key={ev.event_key}
                    className={`border-b border-gray-50 transition-colors ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                    } ${isSaving ? 'opacity-70' : ''}`}
                  >
                    <td className="px-5 py-3.5">
                      <div className={`font-medium flex items-center gap-2 ${soon ? 'text-gray-400' : 'text-gray-800'}`}>
                        {ev.label_he}
                        {soon && (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            בקרוב
                          </span>
                        )}
                      </div>
                      {ev.description_he && (
                        <div className="text-xs text-gray-500 mt-0.5">{ev.description_he}</div>
                      )}
                    </td>
                    {CHANNEL_META.map((ch) => (
                      <td key={ch.key} className="px-4 py-3.5 text-center">
                        <div className="flex justify-center">
                          <Toggle
                            checked={soon ? false : ev[ch.key]}
                            onChange={(v) => onToggle(ev.event_key, ch.key, v)}
                            disabled={isSaving || soon}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */

export function NotificationPreferencesTab() {
  const { activeCompanyId } = useCompany();

  const [prefs,   setPrefs]   = useState<NotificationEventPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState<Set<string>>(new Set());
  const [toast,   setToast]   = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccPhones, setCcPhones] = useState<string[]>([]);
  const [ccSaving, setCcSaving] = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    const [prefsResult, ccResult] = await Promise.all([
      notificationPreferencesService.getPreferences(activeCompanyId),
      notificationPreferencesService.getCcContacts(),
    ]);
    setLoading(false);
    if (prefsResult.error || !prefsResult.data) {
      setToast({ type: 'error', text: 'שגיאה בטעינת הגדרות ההתראות' });
      return;
    }
    setPrefs(prefsResult.data);
    setCcEmails(ccResult.data.emails);
    setCcPhones(ccResult.data.phones);
  }, [activeCompanyId]);

  useEffect(() => { load(); }, [load]);

  const saveCcEmails = useCallback(async (next: string[], action: 'add' | 'remove') => {
    setCcSaving(true);
    const { error } = await notificationPreferencesService.updateCcEmails(next);
    setCcSaving(false);
    if (error) {
      setToast({ type: 'error', text: 'שגיאה בשמירת כתובת המייל' });
      return;
    }
    setCcEmails(next);
    setToast({ type: 'success', text: action === 'add' ? 'כתובת המייל נוספה בהצלחה' : 'כתובת המייל הוסרה' });
  }, []);

  const saveCcPhones = useCallback(async (next: string[], action: 'add' | 'remove') => {
    setCcSaving(true);
    const { error } = await notificationPreferencesService.updateCcPhones(next);
    setCcSaving(false);
    if (error) {
      setToast({ type: 'error', text: 'שגיאה בשמירת מספר הטלפון' });
      return;
    }
    setCcPhones(next);
    setToast({ type: 'success', text: action === 'add' ? 'מספר הטלפון נוסף בהצלחה' : 'מספר הטלפון הוסר' });
  }, []);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Toggle handler ──────────────────────────────────────────────────────────

  const handleToggle = useCallback(async (
    eventKey: string,
    channel:  'in_app' | 'email' | 'sms' | 'whatsapp',
    value:    boolean,
  ) => {
    // Optimistic update
    setPrefs((prev) =>
      prev.map((p) => p.event_key === eventKey ? { ...p, [channel]: value } : p),
    );

    setSaving((s) => new Set(s).add(eventKey));

    const pref = prefs.find((p) => p.event_key === eventKey);
    if (!pref) return;

    const channels = {
      in_app:   pref.in_app,
      email:    pref.email,
      sms:      pref.sms,
      whatsapp: pref.whatsapp,
      [channel]: value,
    };

    const { error } = await notificationPreferencesService.upsertPreference(
      activeCompanyId,
      eventKey,
      channels,
    );

    setSaving((s) => { const next = new Set(s); next.delete(eventKey); return next; });

    if (error) {
      // Roll back
      setPrefs((prev) =>
        prev.map((p) => p.event_key === eventKey ? { ...p, [channel]: !value } : p),
      );
      setToast({ type: 'error', text: 'שגיאה בשמירת ההגדרה' });
    }
  }, [prefs, activeCompanyId]);

  // ── Group by module ─────────────────────────────────────────────────────────

  const grouped = prefs.reduce<Record<string, NotificationEventPreference[]>>((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module]!.push(p);
    return acc;
  }, {});

  // b2b.* events are delivered to B2B clients from their own portal settings, not to staff.
  const moduleOrder = ['inventory', 'system', 'cashflow', 'hr'];

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-48 rounded-xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5" dir="rtl">
      {/* Page hint */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <Bell className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 leading-relaxed">
          <strong>הגדרות התראות אישיות</strong> — בחר לאיזה אירועים תקבל התראה ובאיזה ערוץ.
          שינויים נשמרים מיד. הודעות SMS ו-WhatsApp יישלחו לטלפון שבפרופיל שלך ולטלפונים הנוספים שתגדיר למטה.
          אירועים המסומנים "בקרוב" עדיין לא פעילים.
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`flex items-center gap-3 p-3 rounded-lg border text-sm font-medium
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'}`}>
          {toast.type === 'success'
            ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
            : <AlertCircle  className="w-4 h-4 text-red-600 flex-shrink-0" />}
          {toast.text}
        </div>
      )}

      <ExtraContactsCard
        icon={<AtSign className="w-5 h-5 text-gray-600" />}
        itemIcon={<Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        title="מיילים נוספים להתראות"
        description="ניתן להוסיף עד 2 כתובות מייל נוספות שאליהן יישלחו העתקים של כל ההתראות שהפעלת בערוץ המייל."
        placeholder="example@email.com"
        inputType="email"
        values={ccEmails}
        saving={ccSaving}
        normalize={normalizeEmail}
        invalidMessage="כתובת מייל לא תקינה"
        onSave={saveCcEmails}
      />

      <ExtraContactsCard
        icon={<Phone className="w-5 h-5 text-gray-600" />}
        itemIcon={<Smartphone className="w-4 h-4 text-gray-400 flex-shrink-0" />}
        title="טלפונים נוספים להתראות"
        description="ניתן להוסיף עד 2 מספרי טלפון נוספים שאליהם יישלחו העתקים של כל ההתראות שהפעלת בערוצי SMS ו-WhatsApp. ההודעות יישלחו גם אם אין טלפון שמור בפרופיל שלך."
        placeholder="050-1234567"
        inputType="tel"
        values={ccPhones}
        saving={ccSaving}
        normalize={normalizePhone}
        display={displayPhone}
        invalidMessage="מספר טלפון לא תקין"
        onSave={saveCcPhones}
      />

      {/* Channel legend */}
      <div className="flex flex-wrap gap-4 px-1">
        {CHANNEL_META.map((ch) => (
          <div key={ch.key} className={`flex items-center gap-1.5 text-xs font-medium ${ch.color}`}>
            {ch.icon} {ch.label}
          </div>
        ))}
      </div>

      {/* Module sections */}
      {moduleOrder
        .filter((m) => grouped[m]?.length)
        .map((module) => (
          <ModuleSection
            key={module}
            module={module}
            events={grouped[module]!}
            onToggle={handleToggle}
            saving={saving}
          />
        ))}

      {prefs.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          <Bell className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p>לא נמצאו הגדרות התראות</p>
        </div>
      )}
    </div>
  );
}
